"use client";

import { Phone, MessageCircle, Mail, Globe, Copy, Lock } from "lucide-react";
import { toast } from "sonner";
import posthog from "posthog-js";
import { trackIntent } from "../../../_actions/trackIntent";
import { openAuthDialog } from "@/app/_components/account/AuthDialogProvider";
import { createBrowserSupabaseClient } from "@/lib/supabase/clients/browser-client";
import { ContactCapNotice } from "@/app/_components/contact-cap/ContactCapNotice";

// Module-level flag (#486): a gated in-chat contact CTA was tapped, so once the
// visitor verifies (becomes non-anonymous) the chat auto re-requests the
// contact — the already-rendered gated card is a saved result and won't update.
let _pendingContactRevealCarrierId: string | null = null;
export const flagPendingContactReveal = (carrierId: string) => {
  _pendingContactRevealCarrierId = carrierId;
};
/** Returns the carrierId whose contact should be revealed after verification,
 *  or null if none is pending. */
export const consumePendingContactReveal = (): string | null => {
  const v = _pendingContactRevealCarrierId;
  _pendingContactRevealCarrierId = null;
  return v;
};

interface ContactData {
  phone_number: string | null;
  email: string | null;
  whatsapp_number: string | null;
  website: string | null;
}

interface InlineContactCardProps {
  result: {
    hasContact: boolean;
    contact: ContactData | null;
    carrier: { name: string; city: string; state: string } | null;
    gated?: boolean;
    capped?: boolean;
    contactLimit?: number;
  } | null;
  state: string;
  sessionId: string;
  carrierId: string;
  onSendMessage?: (text: string) => void;
}

function ContactButton({
  icon: Icon,
  label,
  value,
  href,
  onCopy,
  variant = "default",
}: {
  icon: typeof Phone;
  label: string;
  value: string;
  href?: string;
  onCopy?: () => void;
  variant?: "default" | "whatsapp";
}) {
  const bgClass =
    variant === "whatsapp"
      ? "bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
      : "bg-gray-50 hover:bg-gray-100 text-gray-700 border-gray-200";

  return (
    <div className={`flex items-center gap-2 p-2 rounded-lg border ${bgClass} transition-colors`}>
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 flex-1 min-w-0"
        >
          <Icon className="h-4 w-4 shrink-0" />
          <span className="text-sm truncate">{value}</span>
        </a>
      ) : (
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Icon className="h-4 w-4 shrink-0" />
          <span className="text-sm truncate">{value}</span>
        </div>
      )}
      {onCopy && (
        <button
          onClick={onCopy}
          className="p-1 hover:bg-white/50 rounded shrink-0"
          aria-label={`Copiar ${label}`}
        >
          <Copy className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

export function InlineContactCard({
  result,
  state,
  sessionId,
  carrierId,
  onSendMessage,
}: InlineContactCardProps) {
  if (state === "partial-call" || state === "input-streaming" || state === "call") {
    return (
      <div className="space-y-2 animate-pulse my-2">
        <div className="h-10 bg-gray-100 rounded-lg" />
        <div className="h-10 bg-gray-100 rounded-lg" />
      </div>
    );
  }

  // Contact wall (#486): an anonymous visitor gets a gated marker instead of
  // PII. Show a verify CTA that records the impression and opens the OTP dialog.
  if (result?.gated) {
    const handleGate = async () => {
      // This card is a saved result and stays clickable in history. If the user
      // has since verified, don't re-open the OTP dialog — re-request the
      // contact so it reveals.
      const supabase = createBrowserSupabaseClient();
      const { data: { user } } = await supabase.auth.getUser();
      if (user && !user.is_anonymous) {
        onSendMessage?.(`[carrier:${carrierId}] Ya verifiqué mi número, pásame el contacto directo`);
        return;
      }
      posthog.capture("contact_gate_shown", {
        carrier_id: carrierId,
        page: "carrier",
        surface: "chat",
      });
      flagPendingContactReveal(carrierId);
      openAuthDialog("signup", undefined, "chat");
    };
    return (
      <button
        onClick={handleGate}
        className="my-2 flex w-full items-start gap-2.5 rounded-lg border border-orange-200 bg-orange-50 px-3 py-2.5 text-left text-orange-700 transition-colors hover:bg-orange-100"
      >
        <Lock className="mt-0.5 h-4 w-4 shrink-0" />
        <span className="flex flex-col gap-0.5">
          <span className="text-sm font-medium">
            Verifica tu número para ver el contacto directo
          </span>
          <span className="text-xs text-orange-600/90">
            Gratis y en 10 segundos. Verificamos que eres real para proteger a
            los transportistas del spam.
          </span>
        </span>
      </button>
    );
  }

  // Contact cap reached (#487, spec §6) — cap notice replaces the data.
  if (result?.capped) {
    return (
      <ContactCapNotice
        limit={result.contactLimit ?? 5}
        surface="chat"
        carrierId={carrierId}
      />
    );
  }

  if (!result?.hasContact || !result.contact) {
    return null;
  }

  const { contact } = result;

  const handleCopy = async (value: string, method: "phone" | "email" | "whatsapp" | "website") => {
    await navigator.clipboard.writeText(value);
    toast("Copiado al portapapeles");
    // Best-effort telemetry. Catch the rejection so a stale or non-RSC server
    // action response cannot escape as an unhandled promise.
    trackIntent({ sessionId, carrierId, intentType: "contact_copied", method }).catch(() => {});
  };

  const handleClick = (method: "phone" | "email" | "whatsapp" | "website") => {
    trackIntent({ sessionId, carrierId, intentType: "contact_clicked", method }).catch(() => {});
  };

  return (
    <div className="space-y-1.5 my-2">
      {contact.whatsapp_number && (
        <ContactButton
          icon={MessageCircle}
          label="WhatsApp"
          value={contact.whatsapp_number}
          href={`https://wa.me/${contact.whatsapp_number.replace(/\D/g, "")}`}
          onCopy={() => handleCopy(contact.whatsapp_number!, "whatsapp")}
          variant="whatsapp"
        />
      )}
      {contact.phone_number && (
        <ContactButton
          icon={Phone}
          label="Teléfono"
          value={contact.phone_number}
          href={`tel:${contact.phone_number}`}
          onCopy={() => handleCopy(contact.phone_number!, "phone")}
        />
      )}
      {contact.email && (
        <ContactButton
          icon={Mail}
          label="Email"
          value={contact.email}
          href={`mailto:${contact.email}`}
          onCopy={() => handleCopy(contact.email!, "email")}
        />
      )}
      {contact.website && (
        <ContactButton
          icon={Globe}
          label="Sitio web"
          value={contact.website}
          href={contact.website.startsWith("http") ? contact.website : `https://${contact.website}`}
          onCopy={() => handleCopy(contact.website!, "website")}
        />
      )}
    </div>
  );
}
